import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { PalworldPerfilMe } from '../types'
import { apiService } from '../services/api'
import { useAuth } from './AuthContext'

interface PalworldPerfilContextType {
  perfil: PalworldPerfilMe | null
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const PalworldPerfilContext = createContext<PalworldPerfilContextType | undefined>(undefined)

export const usePalworldPerfil = () => {
  const ctx = useContext(PalworldPerfilContext)
  if (!ctx) throw new Error('usePalworldPerfil deve ser usado dentro de PalworldPerfilProvider')
  return ctx
}

const POLL_INTERVAL_MS = 120_000

export const PalworldPerfilProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user, isAuthenticated } = useAuth()

  const roles = useMemo(() => (user?.roles ?? []).map((r) => String(r).toLowerCase()), [user?.roles])
  const isAdmin = roles.some((r) => ['admin', 'administrator'].includes(r))
  const isUser = roles.some((r) => r === 'user' || r === 'role_user')
  const enabled = isAuthenticated && !!user && isUser && !isAdmin

  const [perfil, setPerfil] = useState<PalworldPerfilMe | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const inFlightRef = useRef<Promise<void> | null>(null)
  const hasLoadedRef = useRef(false)

  const refresh = useCallback(async () => {
    if (!enabled) return
    if (inFlightRef.current) return inFlightRef.current

    const run = (async () => {
      setError(null)
      setIsLoading((prev) => prev || !hasLoadedRef.current)
      try {
        const p = await apiService.getMeuPerfilPalworld()
        setPerfil(p)
        hasLoadedRef.current = true
      } catch (e: any) {
        setError(e?.message || 'Erro ao consultar perfil do usuário.')
      } finally {
        setIsLoading(false)
        inFlightRef.current = null
      }
    })()

    inFlightRef.current = run
    return run
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      setPerfil(null)
      setError(null)
      setIsLoading(false)
      hasLoadedRef.current = false
      return
    }

    void refresh()

    const id = window.setInterval(() => {
      void refresh()
    }, POLL_INTERVAL_MS)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    const onFocus = () => void refresh()

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onFocus)

    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onFocus)
    }
  }, [enabled, refresh])

  const value = useMemo<PalworldPerfilContextType>(
    () => ({
      perfil,
      isLoading,
      error,
      refresh
    }),
    [error, isLoading, perfil, refresh]
  )

  return <PalworldPerfilContext.Provider value={value}>{children}</PalworldPerfilContext.Provider>
}
